import { motion } from "framer-motion";
import { Check, Trash2, Tag, Pencil } from "lucide-react";
import { urgency, dateLabel, URGENCY_META, formatEuro } from "@/lib/dates";
import { cn } from "@/lib/utils";

export default function ProductRow({ product, onResolve, onEdit }) {
  const days = product.days_until;
  const u = urgency(days);
  const meta = URGENCY_META[u];

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: 24, height: 0, marginBottom: 0, transition: { duration: 0.25 } }}
      className={cn(
        "group flex items-center justify-between gap-3 bg-card border border-border border-l-4 rounded-2xl px-4 py-3.5 shadow-[0_4px_20px_rgb(0,0,0,0.03)]",
        "transition-transform hover:-translate-y-[2px]",
        meta.border
      )}
      data-testid={`product-row-${product.id}`}
    >
      {product.image && (
        <img
          src={product.image}
          alt=""
          loading="lazy"
          className="h-12 w-12 sm:h-14 sm:w-14 rounded-xl object-cover bg-secondary shrink-0"
          data-testid={`product-image-${product.id}`}
        />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-semibold truncate">{product.name}</span>
          {product.quantity && (
            <span className="mono text-xs text-muted-foreground shrink-0">· {product.quantity}</span>
          )}
        </div>
        <div className="flex items-center gap-3 mt-1 flex-wrap">
          <span className={cn("mono text-xs font-bold", meta.text)}>{dateLabel(days)}</span>
          <span className="inline-flex items-center gap-1 mono text-[10px] uppercase tracking-mono text-muted-foreground">
            <Tag className="h-3 w-3" /> {product.category}
          </span>
          <span className="mono text-[10px] text-muted-foreground/70">{formatEuro(product.price)}</span>
        </div>
      </div>

      <div className="flex items-center gap-1.5 shrink-0">
        <button
          onClick={() => onEdit(product)}
          data-testid={`edit-product-${product.id}`}
          className="inline-flex items-center justify-center rounded-full border border-border h-9 w-9 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
          aria-label="Modifier"
          title="Modifier"
        >
          <Pencil className="h-4 w-4" />
        </button>
        <button
          onClick={() => onResolve(product, "used")}
          data-testid={`mark-used-${product.id}`}
          className="inline-flex items-center justify-center sm:justify-start gap-1 rounded-full border border-border h-9 w-9 sm:w-auto sm:px-3 sm:py-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:bg-primary hover:text-primary-foreground hover:border-primary"
          aria-label="Marquer comme utilisé"
          title="Marquer comme utilisé"
        >
          <Check className="h-4 w-4 sm:h-3.5 sm:w-3.5" /> <span className="hidden sm:inline">Utilisé</span>
        </button>
        <button
          onClick={() => onResolve(product, "wasted")}
          data-testid={`mark-wasted-${product.id}`}
          className="inline-flex items-center justify-center sm:justify-start gap-1 rounded-full border border-border h-9 w-9 sm:w-auto sm:px-3 sm:py-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:bg-destructive hover:text-destructive-foreground hover:border-destructive"
          aria-label="Marquer comme jeté"
          title="Marquer comme jeté"
        >
          <Trash2 className="h-4 w-4 sm:h-3.5 sm:w-3.5" /> <span className="hidden sm:inline">Jeté</span>
        </button>
      </div>
    </motion.div>
  );
}
